import type { DenyReason, IsoInstant } from '@selogate/core'
import { formatDate } from '../i18n/format'
import { useTranslation } from '../i18n/I18nProvider'
import { Icon } from './icons/Icon'

/**
 * The client mirror of `evaluateAccess()` (contracts.md §10), ported as the presentation
 * layer PRD §3 describes: it renders the reason a command would be refused so a card can
 * grey and an actuator can disable before a round trip. **It never grants, and cached client
 * state is never evidence** — the server's `403`/`409` on the same `DenyReason` is the only
 * authority. `DenyReason` is imported from `@selogate/core`, never re-declared; a fifth
 * clause needs a parent decision and one edit in `contracts.md`, not a local addition here.
 *
 * D87 refuses every reserved operating state at the contract layer, so a latched gate is
 * always rendered as blocked/red — there is no "currently open, control disabled" variant
 * left to distinguish (see `flows.md`'s 2026-08-02 correction).
 */
export function AccessBlockLabel({
  reason,
  grantExpiredOn,
}: {
  readonly reason: DenyReason | null
  /** Only meaningful for `grant-expired`; omit to show the reasonless variant. */
  readonly grantExpiredOn?: IsoInstant
}): JSX.Element | null {
  const { t, locale } = useTranslation('access')
  if (reason === null) return null

  const text = ((): string => {
    switch (reason) {
      case 'subscription-expired':
        return t('subscriptionExpired')
      case 'grant-expired':
        return grantExpiredOn === undefined
          ? t('grantExpired')
          : t('grantExpiredOn', { date: formatDate(grantExpiredOn, locale) })
      case 'grant-out-of-schedule':
        return t('grantOutOfSchedule')
      case 'gate-latched':
        return t('gateLatched')
      case 'transport-not-permitted':
        return t('transportNotPermitted')
    }
  })()

  return (
    <div className="shield-line closed">
      <Icon name="shield" size={12} />
      <span>{text}</span>
    </div>
  )
}
